import {useEffect, useState} from "react";
import Head from "next/head"

import {GetStaticProps, NextPage} from "next"
import {MainLayout} from "../../src/components/main-layout";
import {serverSideTranslations} from "next-i18next/serverSideTranslations";
import {getTrips, fetchInterestedDivers} from "../../src/slices/trips-slice";
import {useDispatch, useSelector, wrapper} from "../../src/store";
import {useTranslation} from "next-i18next";
import {Container, CircularProgress, Fade, Box, Hidden, Grid, Button, Menu, MenuItem, Drawer} from "@mui/material";
import {SentimentDissatisfied, FilterList} from "@mui/icons-material";
import {TripResultStatic} from "../../src/components/explorer/trip-result-static";
import {CurrencyChooser} from "../../src/components/currency-chooser";
import {SearchBar} from "../../src/components/explorer/search-bar";
import availCurrencies from "../../src/generated/avail-currencies.json";
import {setSelectedCurrency} from "../../src/slices/persisted-slice";
import {BottomMobileNav} from "../../src/components/bottom-mobile-nav";

interface TripExplorerProps {

    // imagesByTripId: { [key: string]: string[] }
}

const TripExplorer: NextPage<TripExplorerProps> = ({}) => {

    const dispatch = useDispatch();

    const [drawerOpen, setDrawerOpen] = useState(false)

    const [currencyMenuAnchor, setCurrencyMenuAnchor] = useState<HTMLButtonElement | null>(null);

    const {trips, isLoaded, selectedCurrency, interestedByTripId} = useSelector(state => {
        const selectedContinent = state.trips.criteria.selectedContinent;
        const selectedPeriod = state.trips.criteria.selectedPeriod;

        const [diveYear, diveMonth] = selectedPeriod.split("-");


        const filtered = state.trips.trips.tripsArray.filter((t) => {
            // console.log(t.continent.continentId)
            if (selectedContinent && (selectedContinent !== t.continent.continentId || t.continent.continentId == "featured")) return false
            if (selectedPeriod && !t.bestMonths[diveMonth]) return false
            return true
        }).map((trip) => {
            if (state.trips.interestedByTripId && state.trips.interestedByTripId[trip.tripId]) {
                return Object.assign({}, trip, {interested: state.trips.interestedByTripId[trip.tripId]})
            } else {
                return trip
            }
        }).sort((a, b) => {
            return (a.featured === b.featured) ? 0 : a.featured ? -1 : 1;


        })

        return {
            trips: filtered,
            isLoaded: state.trips.isLoaded,
            selectedCurrency: state.persisted.selectedCurrency,
            interestedByTripId: state.trips.interestedByTripId
        }
    });

    const {t, i18n} = useTranslation();


    useEffect(
        () => {
            dispatch(getTrips({language: i18n.language}));
        },
        [i18n.language]
    );

    useEffect(
        () => {
            if (isLoaded) {
                dispatch(fetchInterestedDivers(trips.map((trip) => trip.tripId)))
            }
        },
        [isLoaded]
    );

    const handleCurrencyMenuClose = () => {
        setCurrencyMenuAnchor(null);
    };


    return (
        <>
            <Head>
                <title>{t('trip-explorer') + " | SeaCrush"}</title>
            </Head>


            <Hidden mdDown>
                <SearchBar/>
            </Hidden>

            <section>
                <Container maxWidth='lg' sx={{
                    minHeight: 300,
                    mt: {
                        xs: 3,
                        sm: 0
                    },
                    px: {
                        xs: 0,
                        md: 1
                    }
                }}>

                    <Hidden mdUp>
                        <Box sx={{
                            position: "sticky",
                            zIndex: 1,
                            top: {
                                sx: 49
                            },
                            // bottom: -50,
                            // height: 104,
                            width: "100%",
                            background: "#eee",
                            // borderTop: "1px solid #aaa",
                            boxShadow: "0px 2px 2px rgba(0, 0, 0, .16)"
                        }}>
                            <Grid
                                container
                                spacing={0}
                                alignItems='center'
                                justifyContent={"space-between"}
                                alignContent={"stretch"}
                            >
                                <Grid item>
                                    <Button
                                        sx={{paddingTop: "unset", paddingBottom: "unset"}}
                                        onClick={(e) => setDrawerOpen(true)}
                                        startIcon={<FilterList/>}
                                    >

                                        {t("filter")}
                                    </Button>
                                </Grid>
                                <Grid item>
                                    <Button
                                        onClick={(e) => {
                                            setCurrencyMenuAnchor(e.currentTarget)
                                        }}
                                    >
                                        {selectedCurrency}
                                    </Button>

                                    <Menu
                                        id='currency-menu'
                                        anchorEl={currencyMenuAnchor}
                                        open={Boolean(currencyMenuAnchor)}
                                        onClose={handleCurrencyMenuClose}
                                    >
                                        {Object.keys(availCurrencies)
                                            .sort()
                                            .map((currencyKey, index) => (
                                                <MenuItem
                                                    key={currencyKey}
                                                    selected={currencyKey === selectedCurrency}
                                                    onClick={(event) => {
                                                        event.preventDefault()
                                                        dispatch(setSelectedCurrency(currencyKey))
                                                        handleCurrencyMenuClose();
                                                    }}
                                                >
                                                    <strong style={{fontFamily: "monospace"}}>
                                                        {currencyKey}
                                                    </strong>
                                                    &nbsp;-&nbsp;{(availCurrencies as any)[currencyKey]}
                                                </MenuItem>
                                            ))}
                                    </Menu>
                                </Grid>
                            </Grid>
                        </Box>
                    </Hidden>


                    <Box textAlign={"right"} sx={{mx: 2}}>
                        <Hidden smDown>
                            <CurrencyChooser/>
                        </Hidden>
                    </Box>


                    {!isLoaded && (
                        <CircularProgress
                            sx={{display: "block", margin: "60px auto"}}
                            size={70}
                            thickness={2}
                        />
                    )}

                    {isLoaded && trips.length === 0 && (
                        <Box textAlign={"center"}>
                            <SentimentDissatisfied
                                fontSize={"large"}
                                style={{marginRight: 4, marginBottom: 8}}
                            />
                            <span
                                dangerouslySetInnerHTML={{__html: t("no-results-found")}}
                            />
                        </Box>
                    )}

                    {isLoaded && trips.map((trip, idx) => (
                        <Fade in={true} timeout={900} mountOnEnter={true} key={trip.tripId}>
                            <div>
                                <TripResultStatic diveTrip={trip}
                                                  key={idx}/>
                            </div>
                        </Fade>
                    ))}

                    <Drawer
                        anchor='top'
                        open={drawerOpen}
                        onClose={() => setDrawerOpen(false)}
                    >
                        <Box sx={{mt: 6}}>
                            <SearchBar/>
                            <Button
                                onClick={(e) => setDrawerOpen(false)}
                                fullWidth
                            >{t("close")}</Button>
                        </Box>
                    </Drawer>

                </Container>
            </section>
        </>
    )
}


TripExplorer.getLayout = (page) => (
    <MainLayout>
        {page}
        <BottomMobileNav/>
    </MainLayout>
);

export default TripExplorer

export const getStaticProps: GetStaticProps = wrapper.getStaticProps(store => async ({params, locale}) => {
        if (locale === "default") {
            return {notFound: true};
        }
        // await store.dispatch(fetchInterestedDivers(Object.keys(tripDatabase)))

        // const tripSlugs = await getAllTripPaths([locale as string])

        // const imagesByTripId = getAllTripImagePaths()
        return {
            props: {
                // tripSlugs,
                // imagesByTripId,
                ...(await serverSideTranslations(locale as string, ["frontend"]))
            }
        };
    }
)
